// src/pages/admin/SupportDashboardPage.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useRedux';
import { getSupportConversations, getConversation, Conversation, updateConversationTags, getSupportMetrics } from '../../services/chatService';
import userService from '../../services/userService';
import { useUI } from '../../context/UIContext';
import { getTicketTemplates, createTicket, getTickets, getTicket, updateTicket, Ticket, TicketTemplate } from '../../services/ticketService';
import { format, parseISO, isToday, isYesterday } from 'date-fns';
import {
  ChatIcon,
  PersonIcon,
  CalendarIcon,
  AlertIcon,
  RefreshIcon,
  EmailIcon,
  TimeIcon,
  ListIcon,
  ArrowRightIcon,
  SearchIcon,
  FilterIcon,
  CloseIcon,
  EditIcon,
} from '../../components/common/Icons';
import '../../styles/SupportDashboardPage.css';

interface User {
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  userType: 'client' | 'stylist' | 'admin';
  emailVerified: boolean;
  profilePhoto?: string;
}

interface QuickTemplate {
  id: string;
  title: string;
  content: string;
  category: string;
}

const QUICK_TEMPLATES: QuickTemplate[] = [
  {
    id: 'email-verification',
    title: 'Email Verification Help',
    content: 'Hi! I can help you verify your email. Please check your inbox (and spam folder) for a verification email from us. If you haven\'t received it, I can resend it for you. Would you like me to do that?',
    category: 'Account'
  },
  {
    id: 'password-reset',
    title: 'Password Reset',
    content: 'I can help you reset your password. Please use the "Forgot Password" link on the login page, or I can send you a password reset email right now. Would you like me to send it?',
    category: 'Account'
  },
  {
    id: 'appointment-cancellation',
    title: 'Appointment Cancellation',
    content: 'I understand you need to cancel your appointment. You can cancel it directly from your appointments page, or I can help you cancel it now. Please note that cancellation policies may apply depending on how close we are to the appointment time.',
    category: 'Appointments'
  },
  {
    id: 'appointment-reschedule',
    title: 'Reschedule Appointment',
    content: 'I\'d be happy to help you reschedule your appointment. You can reschedule it from your appointments page, or I can help you find a new time slot. What date and time would work better for you?',
    category: 'Appointments'
  },
  {
    id: 'payment-issue',
    title: 'Payment/Refund Inquiry',
    content: 'I can help you with payment or refund questions. Could you please provide me with your appointment ID or the date of the service? This will help me look up your transaction details.',
    category: 'Billing'
  },
  {
    id: 'technical-issue',
    title: 'Technical Support',
    content: 'I\'m sorry you\'re experiencing technical issues. To help me assist you better, could you please tell me:\n1. What were you trying to do?\n2. What error message (if any) did you see?\n3. What device/browser are you using?',
    category: 'Technical'
  },
  {
    id: 'account-access',
    title: 'Account Access Issues',
    content: 'I can help you regain access to your account. First, let\'s try resetting your password. If that doesn\'t work, I can verify your identity and help you recover your account. What email address is associated with your account?',
    category: 'Account'
  },
  {
    id: 'service-question',
    title: 'Service Question',
    content: 'I\'d be happy to help answer your question about our services. Could you please provide more details about what you\'re looking for? This will help me give you the most accurate information.',
    category: 'General'
  }
];

const SupportDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const { showSnackbar } = useUI();
  // Read through a ref so a new showSnackbar can never re-run the fetch
  // effects below: an error snackbar must not trigger another fetch round.
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [lastSender, setLastSender] = useState<Record<string, string>>({});
  const [lastMessageTime, setLastMessageTime] = useState<Record<string, string>>({});
  const [metrics, setMetrics] = useState<any>(null);
  const [, setMetricsLoading] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [editingConversation, setEditingConversation] = useState<string | null>(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    userType: 'all' as 'all' | 'client' | 'stylist',
    unreadOnly: false,
    dateRange: 'all' as 'all' | 'today' | 'week' | 'month',
    priority: 'all' as 'all' | 'low' | 'medium' | 'high' | 'urgent',
    status: 'all' as 'all' | 'open' | 'in-progress' | 'resolved' | 'closed',
    tags: [] as string[],
  });
  const [showTemplates, setShowTemplates] = useState(false);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [selectedTicketTemplate, setSelectedTicketTemplate] = useState<TicketTemplate | null>(null);
  const [ticketDetails, setTicketDetails] = useState('');
  const [ticketPriority, setTicketPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [ticketTemplates, setTicketTemplates] = useState<TicketTemplate[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [showTickets, setShowTickets] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketDetailsLoading, setTicketDetailsLoading] = useState(false);

  const agentId = user?.id || (user as any)?._id;
  const isSupportAgent =
    user?.userType === 'admin' &&
    (user as any)?.adminRole === 'supportAgent';

  // Fetch support dashboard data
  const fetchSupportDashboard = useCallback(async () => {
    if (!agentId || !isSupportAgent) return;
    
    try {
      setLoading(true);
      const convos = await getSupportConversations();
      setConversations(convos);

      const newUnread: Record<string, number> = {};
      const newLastSender: Record<string, string> = {};
      const newLastMessageTime: Record<string, string> = {};

      // For each conversation, fetch messages & compute unread + track last message
      for (const convo of convos) {
        const convoDetails = await getConversation(convo._id);
        const msgs = convoDetails.messages;
        let count = 0;

        for (const msg of msgs) {
          if (!msg.readBy.includes(agentId) && msg.sender !== agentId) {
            count++;
          }
        }
        newUnread[convo._id] = count;

        // Determine the last message sender and time
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          newLastSender[convo._id] = lastMsg.sender;
          newLastMessageTime[convo._id] = lastMsg.createdAt;
        } else {
          newLastSender[convo._id] = '';
          newLastMessageTime[convo._id] = convo.createdAt;
        }
      }

      setUnreadCounts(newUnread);
      setLastSender(newLastSender);
      setLastMessageTime(newLastMessageTime);
    } catch (err) {
      console.error('Error fetching support dashboard:', err);
      showSnackbarRef.current('Failed to fetch support conversations', 'error');
    } finally {
      setLoading(false);
    }
  }, [agentId, isSupportAgent]);

  // Fetch ticket templates
  const fetchTicketTemplates = useCallback(async () => {
    if (!isSupportAgent) return;
    try {
      const templates = await getTicketTemplates();
      setTicketTemplates(templates);
    } catch (err) {
      console.error('Error fetching ticket templates:', err);
    }
  }, [isSupportAgent]);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    if (!isSupportAgent) return;
    try {
      const ticketList = await getTickets();
      setTickets(ticketList);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    }
  }, [isSupportAgent]);

  useEffect(() => {
    fetchSupportDashboard();
    fetchTicketTemplates();
    fetchTickets();
  }, [fetchSupportDashboard, fetchTicketTemplates, fetchTickets]);

  // Fetch ticket details
  const fetchTicketDetails = useCallback(async (ticketId: string) => {
    if (!isSupportAgent) return;
    try {
      setTicketDetailsLoading(true);
      const ticket = await getTicket(ticketId);
      setSelectedTicket(ticket);
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      showSnackbarRef.current('Failed to load ticket details', 'error');
    } finally {
      setTicketDetailsLoading(false);
    }
  }, [isSupportAgent]);

  // Fetch support metrics
  const fetchMetrics = useCallback(async () => {
    if (!isSupportAgent) return;
    try {
      setMetricsLoading(true);
      const metricsData = await getSupportMetrics();
      setMetrics(metricsData);
    } catch (err) {
      console.error('Error fetching metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, [isSupportAgent]);

  useEffect(() => {
    if (isSupportAgent) {
      fetchMetrics();
      // Refresh metrics every 5 minutes
      const interval = setInterval(fetchMetrics, 300000);
      return () => clearInterval(interval);
    }
  }, [fetchMetrics, isSupportAgent]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!isSupportAgent) return;
    
    const interval = setInterval(() => {
      fetchSupportDashboard();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchSupportDashboard, isSupportAgent]);

  // User search with debounce
  useEffect(() => {
    if (!userSearchQuery.trim()) {
      setUserSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setUserSearchLoading(true);
        const results = await userService.searchUsers(userSearchQuery, { limit: 10 });
        setUserSearchResults(results);
      } catch (err) {
        console.error('Error searching users:', err);
        showSnackbarRef.current('Failed to search users', 'error');
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchQuery]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSupportDashboard();
    setRefreshing(false);
  };

  // Filter and search conversations
  const filteredConversations = useMemo(() => {
    let filtered = [...conversations];

    // Apply user type filter
    if (filters.userType !== 'all') {
      filtered = filtered.filter(convo => {
        const otherParticipant = convo.participants?.find((p: any) => {
          const id = typeof p === 'string' ? p : (p?._id || p);
          return id !== agentId;
        });
        if (typeof otherParticipant === 'object') {
          return (otherParticipant as any).userType === filters.userType;
        }
        return false;
      });
    }

    // Apply priority filter
    if (filters.priority !== 'all') {
      filtered = filtered.filter(convo => convo.priority === filters.priority);
    }

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(convo => convo.status === filters.status);
    }

    // Apply tags filter
    if (filters.tags.length > 0) {
      filtered = filtered.filter(convo => {
        if (!convo.tags || convo.tags.length === 0) return false;
        return filters.tags.some(tag => convo.tags?.includes(tag));
      });
    }

    // Apply unread filter
    if (filters.unreadOnly) {
      filtered = filtered.filter(convo => (unreadCounts[convo._id] || 0) > 0);
    }

    // Apply date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      if (filters.dateRange === 'today') {
        cutoffDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'week') {
        cutoffDate.setDate(now.getDate() - 7);
      } else if (filters.dateRange === 'month') {
        cutoffDate.setMonth(now.getMonth() - 1);
      }
      filtered = filtered.filter(convo => {
        const convoDate = new Date(lastMessageTime[convo._id] || convo.updatedAt);
        return convoDate >= cutoffDate;
      });
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(convo => {
        const otherParticipant = convo.participants?.find((p: any) => {
          const id = typeof p === 'string' ? p : (p?._id || p);
          return id !== agentId;
        });
        if (typeof otherParticipant === 'object') {
          const participant = otherParticipant as any;
          const name = (participant.name || '').toLowerCase();
          const email = (participant.email || '').toLowerCase();
          return name.includes(query) || email.includes(query);
        }
        return false;
      });
    }

    return filtered;
  }, [conversations, filters, searchQuery, unreadCounts, lastMessageTime, agentId]);

  // Calculate dashboard stats
  const stats = {
    totalConversations: conversations.length,
    unreadConversations: Object.values(unreadCounts).filter(count => count > 0).length,
    totalUnreadMessages: Object.values(unreadCounts).reduce((sum, count) => sum + count, 0),
    needsReply: Object.entries(lastSender).filter(([id, sender]) => 
      sender && sender !== agentId && (unreadCounts[id] || 0) === 0
    ).length,
  };

  // Format last message time
  const formatLastMessageTime = (timestamp: string) => {
    try {
      const date = parseISO(timestamp);
      if (isToday(date)) {
        return format(date, 'h:mm a');
      } else if (isYesterday(date)) {
        return 'Yesterday';
      } else {
        return format(date, 'MMM d');
      }
    } catch {
      return '';
    }
  };

  // Handle user search result click
  const handleUserClick = async (userId: string) => {
    try {
      // Find or create a conversation with this user
      const existingConvo = conversations.find(convo => 
        convo.participants.some((p: any) => {
          const id = typeof p === 'string' ? p : (p?._id || p);
          return id === userId;
        })
      );

      if (existingConvo) {
        navigate(`/admin/chat/${existingConvo._id}`);
      } else {
        // Create new conversation
        const { createConversation } = await import('../../services/chatService');
        const newConvo = await createConversation([agentId, userId], 'support');
        navigate(`/admin/chat/${newConvo._id}`);
      }
      setShowUserSearch(false);
      setUserSearchQuery('');
    } catch (err) {
      console.error('Error opening conversation:', err);
      showSnackbar('Failed to open conversation', 'error');
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template: QuickTemplate) => {
    // Copy template to clipboard
    navigator.clipboard.writeText(template.content).then(() => {
      showSnackbar(`Template "${template.title}" copied to clipboard!`, 'success');
      setShowTemplates(false);
    }).catch(() => {
      showSnackbar('Failed to copy template', 'error');
    });
  };

  // Handle updating conversation tags/priority/status
  const handleUpdateConversation = async (
    conversationId: string,
    updates: { 
      tags?: string[]; 
      priority?: 'low' | 'medium' | 'high' | 'urgent'; 
      status?: 'open' | 'in-progress' | 'resolved' | 'closed' 
    }
  ) => {
    try {
      await updateConversationTags(conversationId, updates);
      // Refresh conversations
      await fetchSupportDashboard();
      showSnackbar('Conversation updated successfully', 'success');
      setEditingConversation(null);
    } catch (err: any) {
      console.error('Error updating conversation:', err);
      showSnackbar(err.message || 'Failed to update conversation', 'error');
    }
  };

  // Handle create ticket from conversation
  const handleCreateTicket = async (conversationId: string, userId: string) => {
    try {
      setShowTicketDialog(true);
      // Store conversation and user info for ticket creation
      (window as any).__pendingTicketData = { conversationId, userId };
    } catch (err: any) {
      console.error('Error opening ticket dialog:', err);
      showSnackbar('Failed to open ticket creation', 'error');
    }
  };

  // Handle submit ticket
  const handleSubmitTicket = async () => {
    if (!selectedTicketTemplate) {
      showSnackbar('Please select a ticket template', 'error');
      return;
    }

    const pendingData = (window as any).__pendingTicketData;
    if (!pendingData || !pendingData.userId) {
      showSnackbar('Missing user information', 'error');
      return;
    }

    try {
      setCreatingTicket(true);
      await createTicket({
        relatedUser: pendingData.userId,
        relatedConversation: pendingData.conversationId,
        templateId: selectedTicketTemplate.id,
        priority: ticketPriority,
        details: ticketDetails,
        category: selectedTicketTemplate.category,
        tags: selectedTicketTemplate.tags,
      });
      
      showSnackbar('Ticket created successfully!', 'success');
      setShowTicketDialog(false);
      setSelectedTicketTemplate(null);
      setTicketDetails('');
      setTicketPriority('medium');
      delete (window as any).__pendingTicketData;
      await fetchTickets();
    } catch (err: any) {
      console.error('Error creating ticket:', err);
      showSnackbar(err.message || 'Failed to create ticket', 'error');
    } finally {
      setCreatingTicket(false);
    }
  };

  // Render conversation card
  const renderConversationItem = (convo: Conversation) => {
    // Find the other participant (the user, not the agent)
    const otherParticipant = convo.participants?.find((p: any) => {
      const id = typeof p === 'string' ? p : (p?._id || p);
      return id !== agentId;
    });
    
    let roleLabel = 'Unknown';
    let participantName = 'Unknown User';
    
    if (otherParticipant) {
      if (typeof otherParticipant === 'object') {
        const participant = otherParticipant as any;
        if (participant.userType === 'client') {
          roleLabel = 'Client';
        } else if (participant.userType === 'stylist') {
          roleLabel = 'Stylist';
        }
        participantName = participant.name || 'Unknown User';
      }
    }
    
    const participantLabel = `${roleLabel} ${participantName}`;

    const unreadCount = unreadCounts[convo._id] || 0;
    const lastMsgSender = lastSender[convo._id] || '';
    const lastMsgTime = lastMessageTime[convo._id] || '';

    // Decide which badge to show
    let badge = null;
    if (unreadCount > 0) {
      badge = (
        <div className="support-dashboard__badge support-dashboard__badge--unread">
          <EmailIcon size={16} color="#FFF" />
          <span>{unreadCount}</span>
        </div>
      );
    } else if (lastMsgSender && lastMsgSender !== agentId) {
      badge = (
        <div className="support-dashboard__badge support-dashboard__badge--reply">
          <span>Reply</span>
        </div>
      );
    }

    const priorityColors = {
      low: '#10B981',
      medium: '#F59E0B',
      high: '#C0392B',
      urgent: '#C0392B',
    };

    const statusColors = {
      open: '#C9A84C',
      'in-progress': '#C9A84C',
      resolved: '#10B981',
      closed: '#6B7280',
    };

    // Calculate SLA status (24 hours for first response, 48 hours for resolution)
    const SLA_FIRST_RESPONSE_HOURS = 24;
    const SLA_RESOLUTION_HOURS = 48;
    const now = new Date();
    const createdAt = new Date(convo.createdAt);
    const hoursSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
    
    let slaStatus: 'ok' | 'warning' | 'overdue' = 'ok';
    let slaMessage = '';
    
    if (convo.status === 'open' || convo.status === 'in-progress') {
      if (!convo.metrics?.firstResponseTime) {
        // No first response yet
        if (hoursSinceCreation > SLA_FIRST_RESPONSE_HOURS) {
          slaStatus = 'overdue';
          slaMessage = `Overdue: ${Math.round(hoursSinceCreation - SLA_FIRST_RESPONSE_HOURS)}h past SLA`;
        } else if (hoursSinceCreation > SLA_FIRST_RESPONSE_HOURS * 0.75) {
          slaStatus = 'warning';
          slaMessage = `Approaching SLA: ${Math.round(SLA_FIRST_RESPONSE_HOURS - hoursSinceCreation)}h remaining`;
        }
      } else if (hoursSinceCreation > SLA_RESOLUTION_HOURS) {
        slaStatus = 'overdue';
        slaMessage = `Resolution overdue: ${Math.round(hoursSinceCreation - SLA_RESOLUTION_HOURS)}h past SLA`;
      } else if (hoursSinceCreation > SLA_RESOLUTION_HOURS * 0.75) {
        slaStatus = 'warning';
        slaMessage = `Resolution approaching SLA: ${Math.round(SLA_RESOLUTION_HOURS - hoursSinceCreation)}h remaining`;
      }
    }

    return (
      <div
        key={convo._id}
        className="support-dashboard__conversation-card"
        onClick={(e) => {
          // Don't navigate if clicking on edit controls
          if ((e.target as HTMLElement).closest('.support-dashboard__conversation-edit')) {
            return;
          }
          navigate(`/admin/chat/${convo._id}`);
        }}
      >
        <div className="support-dashboard__conversation-header">
          <div className="support-dashboard__conversation-info">
            <h3 className="support-dashboard__conversation-title">{participantLabel}</h3>
            <div className="support-dashboard__conversation-meta">
              <span className="support-dashboard__conversation-type">{roleLabel}</span>
              {convo.priority && (
                <span
                  className="support-dashboard__conversation-priority"
                  style={{ backgroundColor: priorityColors[convo.priority] + '20', color: priorityColors[convo.priority] }}
                >
                  {convo.priority}
                </span>
              )}
              {convo.status && (
                <span
                  className="support-dashboard__conversation-status"
                  style={{ backgroundColor: statusColors[convo.status] + '20', color: statusColors[convo.status] }}
                >
                  {convo.status}
                </span>
              )}
            </div>
            {convo.tags && convo.tags.length > 0 && (
              <div className="support-dashboard__conversation-tags">
                {convo.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="support-dashboard__conversation-tag">
                    {tag}
                  </span>
                ))}
                {convo.tags.length > 3 && (
                  <span className="support-dashboard__conversation-tag-more">
                    +{convo.tags.length - 3}
                  </span>
                )}
              </div>
            )}
            {slaStatus !== 'ok' && (
              <div 
                className={`support-dashboard__conversation-sla support-dashboard__conversation-sla--${slaStatus}`}
                title={slaMessage}
              >
                <AlertIcon size={14} />
                <span>{slaMessage}</span>
              </div>
            )}
          </div>
          <div className="support-dashboard__conversation-actions">
            {badge}
            <button
              className="support-dashboard__conversation-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                setEditingConversation(editingConversation === convo._id ? null : convo._id);
              }}
              title="Edit tags and priority"
            >
              <EditIcon size={16} />
            </button>
            <button
              className="support-dashboard__conversation-ticket-btn"
              onClick={(e) => {
                e.stopPropagation();
                const otherParticipant = convo.participants?.find((p: any) => {
                  const id = typeof p === 'string' ? p : (p?._id || p);
                  return id !== agentId;
                });
                const userId = typeof otherParticipant === 'object' 
                  ? (otherParticipant as any)?._id || otherParticipant
                  : otherParticipant;
                if (userId) {
                  handleCreateTicket(convo._id, userId);
                }
              }}
              title="Create ticket for this issue"
            >
              <AlertIcon size={16} />
            </button>
          </div>
        </div>
        
        {editingConversation === convo._id && (
          <div className="support-dashboard__conversation-edit" onClick={(e) => e.stopPropagation()}>
            <div className="support-dashboard__edit-group">
              <label>Tags</label>
              <div className="support-dashboard__tag-selector">
                {['billing', 'technical', 'account', 'appointment', 'service', 'payment', 'refund', 'general', 'urgent', 'follow-up'].map(tag => (
                  <label key={tag} className="support-dashboard__tag-checkbox">
                    <input
                      type="checkbox"
                      checked={convo.tags?.includes(tag) || false}
                      onChange={(e) => {
                        const newTags = e.target.checked
                          ? [...(convo.tags || []), tag]
                          : (convo.tags || []).filter(t => t !== tag);
                        handleUpdateConversation(convo._id, { tags: newTags });
                      }}
                    />
                    <span>{tag}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="support-dashboard__edit-group">
              <label>Priority</label>
              <select
                value={convo.priority || 'medium'}
                onChange={(e) => {
                  const priority = e.target.value as 'low' | 'medium' | 'high' | 'urgent';
                  handleUpdateConversation(convo._id, { priority });
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="support-dashboard__edit-group">
              <label>Status</label>
              <select
                value={convo.status || 'open'}
                onChange={(e) => {
                  const status = e.target.value as 'open' | 'in-progress' | 'resolved' | 'closed';
                  handleUpdateConversation(convo._id, { status });
                }}
              >
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        )}

        <div className="support-dashboard__conversation-footer">
          <span className="support-dashboard__conversation-time">
            {lastMsgTime ? formatLastMessageTime(lastMsgTime) : 'No messages'}
          </span>
          <ArrowRightIcon size={20} />
        </div>
      </div>
    );
  };

  // Sort conversations: unread first, then by last message time
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    const unreadA = unreadCounts[a._id] || 0;
    const unreadB = unreadCounts[b._id] || 0;
    
    if (unreadA > 0 && unreadB === 0) return -1;
    if (unreadA === 0 && unreadB > 0) return 1;
    
    const timeA = lastMessageTime[a._id] || a.updatedAt;
    const timeB = lastMessageTime[b._id] || b.updatedAt;
    return new Date(timeB).getTime() - new Date(timeA).getTime();
  });

  if (!isSupportAgent) {
    return (
      <div className="support-dashboard">
        <div className="support-dashboard__error">
          <AlertIcon size={24} />
          <p>You do not have permission to access the support dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="support-dashboard">
      {/* Header */}
      <div className="support-dashboard__header">
        <div>
          <h1 className="support-dashboard__title">Support Dashboard</h1>
          <p className="support-dashboard__subtitle">Manage customer support conversations</p>
        </div>
        <div className="support-dashboard__header-actions">
            <button
              className="support-dashboard__action-btn"
              onClick={() => setShowTickets(!showTickets)}
              title="View Tickets"
            >
              <CalendarIcon size={20} />
              Tickets
              {tickets.filter(t => t.status === 'open' || t.status === 'in-progress').length > 0 && (
                <span className="support-dashboard__action-badge">
                  {tickets.filter(t => t.status === 'open' || t.status === 'in-progress').length}
                </span>
              )}
            </button>
            <button
              className="support-dashboard__action-btn"
              onClick={() => setShowMetrics(!showMetrics)}
              title="Support Metrics"
            >
              <TimeIcon size={20} />
              Metrics
            </button>
            <button
              className="support-dashboard__action-btn"
              onClick={() => setShowTemplates(!showTemplates)}
              title="Quick Response Templates"
            >
              <EmailIcon size={20} />
              Templates
            </button>
            <button
              className="support-dashboard__action-btn"
              onClick={() => navigate('/admin/audit-trails')}
              title="View audit logs"
            >
              <ListIcon size={20} />
              Audit Trails
            </button>
          <button
            className="support-dashboard__refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshIcon size={20} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Support Metrics Panel */}
      {showMetrics && metrics && (
        <div className="support-dashboard__metrics-panel">
          <div className="support-dashboard__metrics-header">
            <h3>Support Metrics</h3>
            <button onClick={() => setShowMetrics(false)}>
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="support-dashboard__metrics-content">
            <div className="support-dashboard__metrics-grid">
              <div className="support-dashboard__metric-item">
                <h4>Average Response Time</h4>
                <p className="support-dashboard__metric-value">
                  {metrics.averageMetrics.firstResponseTime || 0} min
                </p>
              </div>
              <div className="support-dashboard__metric-item">
                <h4>Average Resolution Time</h4>
                <p className="support-dashboard__metric-value">
                  {metrics.averageMetrics.resolutionTime || 0} hrs
                </p>
              </div>
              <div className="support-dashboard__metric-item">
                <h4>Resolution Rate</h4>
                <p className="support-dashboard__metric-value">
                  {metrics.resolutionRate || 0}%
                </p>
              </div>
            </div>
            <div className="support-dashboard__metrics-breakdown">
              <div className="support-dashboard__breakdown-section">
                <h4>Status Breakdown</h4>
                <div className="support-dashboard__breakdown-list">
                  <div className="support-dashboard__breakdown-item">
                    <span>Open</span>
                    <span>{metrics.statusCounts.open}</span>
                  </div>
                  <div className="support-dashboard__breakdown-item">
                    <span>In Progress</span>
                    <span>{metrics.statusCounts.inProgress}</span>
                  </div>
                  <div className="support-dashboard__breakdown-item">
                    <span>Resolved</span>
                    <span>{metrics.statusCounts.resolved}</span>
                  </div>
                  <div className="support-dashboard__breakdown-item">
                    <span>Closed</span>
                    <span>{metrics.statusCounts.closed}</span>
                  </div>
                </div>
              </div>
              <div className="support-dashboard__breakdown-section">
                <h4>Priority Breakdown</h4>
                <div className="support-dashboard__breakdown-list">
                  <div className="support-dashboard__breakdown-item">
                    <span>Low</span>
                    <span>{metrics.priorityCounts.low}</span>
                  </div>
                  <div className="support-dashboard__breakdown-item">
                    <span>Medium</span>
                    <span>{metrics.priorityCounts.medium}</span>
                  </div>
                  <div className="support-dashboard__breakdown-item">
                    <span>High</span>
                    <span>{metrics.priorityCounts.high}</span>
                  </div>
                  <div className="support-dashboard__breakdown-item">
                    <span>Urgent</span>
                    <span>{metrics.priorityCounts.urgent}</span>
                  </div>
                </div>
              </div>
              {Object.keys(metrics.tagCounts).length > 0 && (
                <div className="support-dashboard__breakdown-section">
                  <h4>Top Issue Tags</h4>
                  <div className="support-dashboard__breakdown-list">
                    {Object.entries(metrics.tagCounts)
                      .sort(([, a], [, b]) => (b as number) - (a as number))
                      .slice(0, 5)
                      .map(([tag, count]) => (
                        <div key={tag} className="support-dashboard__breakdown-item">
                          <span>{tag}</span>
                          <span>{count as number}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tickets Panel */}
      {showTickets && (
        <div className="support-dashboard__tickets-panel">
          <div className="support-dashboard__tickets-header">
            <h3>Tickets ({tickets.length})</h3>
            <button onClick={() => setShowTickets(false)}>
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="support-dashboard__tickets-content">
            {tickets.length === 0 ? (
              <div className="support-dashboard__tickets-empty">
                <p>No tickets created yet</p>
              </div>
            ) : (
              <div className="support-dashboard__tickets-list">
                {tickets.map(ticket => (
                  <div 
                    key={ticket._id} 
                    className="support-dashboard__ticket-item"
                    onClick={() => {
                      setShowTicketDetails(true);
                      fetchTicketDetails(ticket._id);
                    }}
                  >
                    <div className="support-dashboard__ticket-header">
                      <div className="support-dashboard__ticket-info">
                        <h4>{ticket.ticketNumber}</h4>
                        <p>{ticket.title}</p>
                      </div>
                      <div className="support-dashboard__ticket-badges">
                        <span className={`support-dashboard__ticket-priority support-dashboard__ticket-priority--${ticket.priority}`}>
                          {ticket.priority}
                        </span>
                        <span className={`support-dashboard__ticket-status support-dashboard__ticket-status--${ticket.status}`}>
                          {ticket.status}
                        </span>
                      </div>
                    </div>
                    <div className="support-dashboard__ticket-meta">
                      <span>{typeof ticket.relatedUser === 'object' ? ticket.relatedUser.name : 'Unknown User'}</span>
                      <span>{format(parseISO(ticket.createdAt), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ticket Details Dialog */}
      {showTicketDetails && selectedTicket && (
        <div className="support-dashboard__ticket-dialog-overlay" onClick={() => setShowTicketDetails(false)}>
          <div className="support-dashboard__ticket-dialog support-dashboard__ticket-dialog--details" onClick={(e) => e.stopPropagation()}>
            <div className="support-dashboard__ticket-dialog-header">
              <h3>Ticket Details</h3>
              <button onClick={() => setShowTicketDetails(false)}>
                <CloseIcon size={20} />
              </button>
            </div>
            {ticketDetailsLoading ? (
              <div className="support-dashboard__ticket-dialog-content">
                <div className="support-dashboard__loading">
                  <div className="spinner"></div>
                  <p>Loading ticket details...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="support-dashboard__ticket-dialog-content">
                  <div className="support-dashboard__ticket-details-section">
                    <div className="support-dashboard__ticket-details-header">
                      <div>
                        <h4>{selectedTicket.ticketNumber}</h4>
                        <p>{selectedTicket.title}</p>
                      </div>
                      <div className="support-dashboard__ticket-badges">
                        <span className={`support-dashboard__ticket-priority support-dashboard__ticket-priority--${selectedTicket.priority}`}>
                          {selectedTicket.priority}
                        </span>
                        <span className={`support-dashboard__ticket-status support-dashboard__ticket-status--${selectedTicket.status}`}>
                          {selectedTicket.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="support-dashboard__ticket-details-section">
                    <h5>Description</h5>
                    <p className="support-dashboard__ticket-description">{selectedTicket.description}</p>
                  </div>

                  <div className="support-dashboard__ticket-details-grid">
                    <div className="support-dashboard__ticket-detail-item">
                      <label>Category</label>
                      <span>{selectedTicket.category}</span>
                    </div>
                    <div className="support-dashboard__ticket-detail-item">
                      <label>Created</label>
                      <span>{format(parseISO(selectedTicket.createdAt), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                    <div className="support-dashboard__ticket-detail-item">
                      <label>Related User</label>
                      <span>{typeof selectedTicket.relatedUser === 'object' ? selectedTicket.relatedUser.name : 'Unknown'}</span>
                    </div>
                    {selectedTicket.assignedTo && (
                      <div className="support-dashboard__ticket-detail-item">
                        <label>Assigned To</label>
                        <span>{typeof selectedTicket.assignedTo === 'object' ? selectedTicket.assignedTo.name : 'Unassigned'}</span>
                      </div>
                    )}
                  </div>

                  {selectedTicket.resolution && (
                    <div className="support-dashboard__ticket-details-section">
                      <h5>Resolution</h5>
                      <p className="support-dashboard__ticket-resolution">{selectedTicket.resolution}</p>
                      {selectedTicket.resolvedAt && (
                        <p className="support-dashboard__ticket-resolution-date">
                          Resolved on {format(parseISO(selectedTicket.resolvedAt), 'MMM d, yyyy h:mm a')}
                        </p>
                      )}
                    </div>
                  )}

                  {selectedTicket.internalNotes && selectedTicket.internalNotes.length > 0 && (
                    <div className="support-dashboard__ticket-details-section">
                      <h5>Internal Notes</h5>
                      <div className="support-dashboard__ticket-notes">
                        {selectedTicket.internalNotes.map((note: any, index: number) => (
                          <div key={index} className="support-dashboard__ticket-note">
                            <div className="support-dashboard__ticket-note-header">
                              <span>{typeof note.addedBy === 'object' ? note.addedBy.name : (typeof note.createdBy === 'object' ? note.createdBy.name : 'Support Agent')}</span>
                              <span>{format(parseISO(note.addedAt || note.createdAt), 'MMM d, h:mm a')}</span>
                            </div>
                            <p>{note.note}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedTicket.tags && selectedTicket.tags.length > 0 && (
                    <div className="support-dashboard__ticket-details-section">
                      <h5>Tags</h5>
                      <div className="support-dashboard__ticket-tags-list">
                        {selectedTicket.tags.map(tag => (
                          <span key={tag} className="support-dashboard__ticket-tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="support-dashboard__ticket-dialog-actions">
                  <button
                    className="support-dashboard__ticket-dialog-cancel"
                    onClick={() => setShowTicketDetails(false)}
                  >
                    Close
                  </button>
                  {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                    <button
                      className="support-dashboard__ticket-dialog-submit"
                      onClick={async () => {
                        try {
                          await updateTicket(selectedTicket._id, { status: 'resolved' });
                          showSnackbar('Ticket marked as resolved', 'success');
                          await fetchTicketDetails(selectedTicket._id);
                          await fetchTickets();
                        } catch (err: any) {
                          showSnackbar(err.message || 'Failed to update ticket', 'error');
                        }
                      }}
                    >
                      Mark as Resolved
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Ticket Dialog */}
      {showTicketDialog && (
        <div className="support-dashboard__ticket-dialog-overlay" onClick={() => setShowTicketDialog(false)}>
          <div className="support-dashboard__ticket-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="support-dashboard__ticket-dialog-header">
              <h3>Create Support Ticket</h3>
              <button onClick={() => setShowTicketDialog(false)}>
                <CloseIcon size={20} />
              </button>
            </div>
            <div className="support-dashboard__ticket-dialog-content">
              <div className="support-dashboard__ticket-form-group">
                <label>Select Template</label>
                <select
                  value={selectedTicketTemplate?.id || ''}
                  onChange={(e) => {
                    const template = ticketTemplates.find(t => t.id === e.target.value);
                    setSelectedTicketTemplate(template || null);
                    if (template) {
                      setTicketPriority(template.priority as any);
                    }
                  }}
                >
                  <option value="">Select a template...</option>
                  {ticketTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.priority})
                    </option>
                  ))}
                </select>
              </div>
              {selectedTicketTemplate && (
                <>
                  <div className="support-dashboard__ticket-form-group">
                    <label>Priority</label>
                    <select
                      value={ticketPriority}
                      onChange={(e) => setTicketPriority(e.target.value as any)}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div className="support-dashboard__ticket-form-group">
                    <label>Additional Details</label>
                    <textarea
                      value={ticketDetails}
                      onChange={(e) => setTicketDetails(e.target.value)}
                      placeholder="Add any additional details or context..."
                      rows={4}
                    />
                  </div>
                  <div className="support-dashboard__ticket-preview">
                    <h4>Preview:</h4>
                    <p><strong>Title:</strong> {selectedTicketTemplate.title}</p>
                    <p><strong>Category:</strong> {selectedTicketTemplate.category}</p>
                    <p><strong>Priority:</strong> {ticketPriority}</p>
                  </div>
                </>
              )}
            </div>
            <div className="support-dashboard__ticket-dialog-actions">
              <button
                className="support-dashboard__ticket-dialog-cancel"
                onClick={() => setShowTicketDialog(false)}
              >
                Cancel
              </button>
              <button
                className="support-dashboard__ticket-dialog-submit"
                onClick={handleSubmitTicket}
                disabled={!selectedTicketTemplate || creatingTicket}
              >
                {creatingTicket ? 'Creating...' : 'Create Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Templates Panel */}
      {showTemplates && (
        <div className="support-dashboard__templates-panel">
          <div className="support-dashboard__templates-header">
            <h3>Quick Response Templates</h3>
            <button onClick={() => setShowTemplates(false)}>
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="support-dashboard__templates-grid">
            {QUICK_TEMPLATES.map(template => (
              <div
                key={template.id}
                className="support-dashboard__template-card"
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="support-dashboard__template-category">{template.category}</div>
                <h4>{template.title}</h4>
                <p>{template.content.substring(0, 100)}...</p>
                <div className="support-dashboard__template-action">Click to copy</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="support-dashboard__stats">
        <div className="support-dashboard__stat-card">
          <div className="support-dashboard__stat-icon support-dashboard__stat-icon--primary">
            <ChatIcon size={24} />
          </div>
          <div className="support-dashboard__stat-content">
            <h3 className="support-dashboard__stat-value">{stats.totalConversations}</h3>
            <p className="support-dashboard__stat-label">Total Conversations</p>
          </div>
        </div>

        <div className="support-dashboard__stat-card">
          <div className="support-dashboard__stat-icon support-dashboard__stat-icon--warning">
            <EmailIcon size={24} />
          </div>
          <div className="support-dashboard__stat-content">
            <h3 className="support-dashboard__stat-value">{stats.unreadConversations}</h3>
            <p className="support-dashboard__stat-label">Unread Conversations</p>
          </div>
        </div>

        <div className="support-dashboard__stat-card">
          <div className="support-dashboard__stat-icon support-dashboard__stat-icon--danger">
            <AlertIcon size={24} />
          </div>
          <div className="support-dashboard__stat-content">
            <h3 className="support-dashboard__stat-value">{stats.totalUnreadMessages}</h3>
            <p className="support-dashboard__stat-label">Unread Messages</p>
          </div>
        </div>

        <div className="support-dashboard__stat-card">
          <div className="support-dashboard__stat-icon support-dashboard__stat-icon--info">
            <TimeIcon size={24} />
          </div>
          <div className="support-dashboard__stat-content">
            <h3 className="support-dashboard__stat-value">{stats.needsReply}</h3>
            <p className="support-dashboard__stat-label">Needs Reply</p>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="support-dashboard__search-bar">
        <div className="support-dashboard__search-group">
          <SearchIcon size={20} />
          <input
            type="text"
            placeholder="Search conversations by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="support-dashboard__search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="support-dashboard__search-clear"
            >
              <CloseIcon size={16} />
            </button>
          )}
        </div>
        <button
          className="support-dashboard__user-search-btn"
          onClick={() => setShowUserSearch(!showUserSearch)}
        >
          <PersonIcon size={20} />
          Find User
        </button>
        <button
          className="support-dashboard__filter-btn"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FilterIcon size={20} />
          Filters
          {(filters.userType !== 'all' || filters.unreadOnly || filters.dateRange !== 'all' || 
            filters.priority !== 'all' || filters.status !== 'all' || filters.tags.length > 0) && (
            <span className="support-dashboard__filter-badge">•</span>
          )}
        </button>
      </div>

      {/* User Search Dropdown */}
      {showUserSearch && (
        <div className="support-dashboard__user-search-panel">
          <div className="support-dashboard__user-search-header">
            <h3>Find User</h3>
            <button onClick={() => setShowUserSearch(false)}>
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="support-dashboard__user-search-input-wrapper">
            <SearchIcon size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="support-dashboard__user-search-input"
              autoFocus
            />
          </div>
          {userSearchLoading && (
            <div className="support-dashboard__user-search-loading">
              <div className="spinner"></div>
              <p>Searching...</p>
            </div>
          )}
          {!userSearchLoading && userSearchResults.length > 0 && (
            <div className="support-dashboard__user-search-results">
              {userSearchResults.map((user) => (
                <div
                  key={user._id}
                  className="support-dashboard__user-search-result"
                  onClick={() => handleUserClick(user._id)}
                >
                  <div className="support-dashboard__user-search-avatar">
                    {user.profilePhoto ? (
                      <img src={user.profilePhoto} alt={user.name} />
                    ) : (
                      <span>{user.name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="support-dashboard__user-search-info">
                    <h4>{user.name}</h4>
                    <p>{user.email}</p>
                    <span className="support-dashboard__user-search-type">
                      {user.userType} {user.emailVerified ? '✓' : '⚠'}
                    </span>
                  </div>
                  <ArrowRightIcon size={20} />
                </div>
              ))}
            </div>
          )}
          {!userSearchLoading && userSearchQuery && userSearchResults.length === 0 && (
            <div className="support-dashboard__user-search-empty">
              <p>No users found</p>
            </div>
          )}
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="support-dashboard__filters-panel">
          <div className="support-dashboard__filters-header">
            <h3>Filter Conversations</h3>
            <button onClick={() => setShowFilters(false)}>
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="support-dashboard__filters-content">
            <div className="support-dashboard__filter-group">
              <label>User Type</label>
              <select
                value={filters.userType}
                onChange={(e) => setFilters({ ...filters, userType: e.target.value as any })}
              >
                <option value="all">All Users</option>
                <option value="client">Clients Only</option>
                <option value="stylist">Stylists Only</option>
              </select>
            </div>
            <div className="support-dashboard__filter-group">
              <label>
                <input
                  type="checkbox"
                  checked={filters.unreadOnly}
                  onChange={(e) => setFilters({ ...filters, unreadOnly: e.target.checked })}
                />
                Unread Only
              </label>
            </div>
            <div className="support-dashboard__filter-group">
              <label>Date Range</label>
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as any })}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>
            <div className="support-dashboard__filter-group">
              <label>Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value as any })}
              >
                <option value="all">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="support-dashboard__filter-group">
              <label>Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="support-dashboard__filter-group">
              <label>Tags</label>
              <div className="support-dashboard__tag-filters">
                {['billing', 'technical', 'account', 'appointment', 'service', 'payment', 'refund', 'general', 'urgent', 'follow-up'].map(tag => (
                  <label key={tag} className="support-dashboard__tag-filter-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.tags.includes(tag)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFilters({ ...filters, tags: [...filters.tags, tag] });
                        } else {
                          setFilters({ ...filters, tags: filters.tags.filter(t => t !== tag) });
                        }
                      }}
                    />
                    <span>{tag}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              className="support-dashboard__filter-reset"
              onClick={() => setFilters({ 
                userType: 'all', 
                unreadOnly: false, 
                dateRange: 'all',
                priority: 'all',
                status: 'all',
                tags: []
              })}
            >
              Reset Filters
            </button>
          </div>
        </div>
      )}

      {/* Conversations List */}
      <div className="support-dashboard__conversations-section">
        <div className="support-dashboard__section-header">
          <h2 className="support-dashboard__section-title">Recent Conversations</h2>
          <span className="support-dashboard__section-count">
            {sortedConversations.length} {sortedConversations.length === 1 ? 'conversation' : 'conversations'}
            {filteredConversations.length !== conversations.length && (
              <span className="support-dashboard__filtered-indicator"> (filtered)</span>
            )}
          </span>
        </div>

        {loading ? (
          <div className="support-dashboard__loading">
            <div className="spinner"></div>
            <p>Loading conversations...</p>
          </div>
        ) : sortedConversations.length === 0 ? (
          <div className="support-dashboard__empty">
            <ChatIcon size={48} />
            <h3>No conversations found</h3>
            <p>
              {searchQuery || filters.userType !== 'all' || filters.unreadOnly || filters.dateRange !== 'all' ||
               filters.priority !== 'all' || filters.status !== 'all' || filters.tags.length > 0
                ? 'Try adjusting your search or filters'
                : 'When customers contact support, their conversations will appear here.'}
            </p>
          </div>
        ) : (
          <div className="support-dashboard__conversations">
            {sortedConversations.map(renderConversationItem)}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportDashboardPage;
